import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/TutorNavbar.css';
import logo from '../assets/images/jj-tutorial-logo.png.png';
import NotificationBell from './NotificationBell';
import UserMenu from './UserMenu';

const TutorNavbar = ({ activeTab, setActiveTab, user, onLogout }) => {
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
      if (window.innerWidth >= 768) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setIsMobileMenuOpen(false); // Close mobile menu on navigation
    // Navigate to the appropriate route
    if (tab === 'submit') {
      navigate('/tutor/submit');
    } else if (tab === 'tutees') {
      navigate('/tutor/tutees');
    } else if (tab === 'lessons') {
      navigate('/tutor/lessons');
    } else if (tab === 'profile') {
      navigate('/tutor/profile');
    } else if (tab === 'claims') {
      navigate('/tutor/claims');
    } else if (tab === 'finance-ic-invoices') {
      navigate('/tutor/finance-ic-invoices');
    } else if (tab === 'admin-tutees') {
      navigate('/tutor/admin-tutees');
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  return (
    <nav className="tutor-navbar" role="navigation" aria-label="Main navigation">
      <div className="navbar-brand">
        <img src={logo} alt="JJ Tutorial logo" className="navbar-logo" />
        <span className="navbar-title">Tutor Portal</span>
      </div>
      
      {isMobile && (
        <button 
          className="hamburger-menu"
          onClick={toggleMobileMenu}
          aria-label="Toggle navigation menu"
          aria-expanded={isMobileMenuOpen}
        >
          <span className={`hamburger-icon ${isMobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      )}
      
      {!isMobile && (
        <>
          <div className="navbar-tabs" role="tablist" aria-label="Main navigation tabs">
            <button 
              className={activeTab === 'submit' ? 'active' : ''}
              onClick={() => handleTabChange('submit')}
              role="tab"
              aria-selected={activeTab === 'submit'}
              aria-label="Submit Attendance"
            >
              Submit Attendance
            </button>
            <button 
              className={activeTab === 'tutees' ? 'active' : ''}
              onClick={() => handleTabChange('tutees')}
              role="tab"
              aria-selected={activeTab === 'tutees'}
              aria-label="My Tutees"
            >
              My Tutees
            </button>
            <button 
              className={activeTab === 'lessons' ? 'active' : ''}
              onClick={() => handleTabChange('lessons')}
              role="tab"
              aria-selected={activeTab === 'lessons'}
              aria-label="Lesson Summary"
            >
              Lesson Summary
            </button>
            <button 
              className={activeTab === 'profile' ? 'active' : ''}
              onClick={() => handleTabChange('profile')}
              role="tab"
              aria-selected={activeTab === 'profile'}
              aria-label="My Profile"
            >
              My Profile
            </button>
            <button 
              className={activeTab === 'claims' ? 'active' : ''}
              onClick={() => handleTabChange('claims')}
              role="tab"
              aria-selected={activeTab === 'claims'}
              aria-label="Claims"
            >
              Claims
            </button>
            {user?.financeICSettings?.isEligible && user?.financeICSettings?.isActive && (
              <button 
                className={activeTab === 'finance-ic-invoices' ? 'active' : ''}
                onClick={() => handleTabChange('finance-ic-invoices')}
                role="tab"
                aria-selected={activeTab === 'finance-ic-invoices'}
                aria-label="Finance IC Invoices"
              >
                Finance IC
              </button>
            )}
            {user?.isCoFounder && (
              <button
                className={activeTab === 'admin-tutees' ? 'active' : ''}
                onClick={() => handleTabChange('admin-tutees')}
                role="tab"
                aria-selected={activeTab === 'admin-tutees'}
                aria-label="All Tutees"
              >
                All Tutees
              </button>
            )}
          </div>
          
          <div className="navbar-user">
            <NotificationBell />
            <UserMenu 
              user={user} 
              onLogout={onLogout}
              onNavigate={handleNavigate}
              showActivityLog={false}
            />
          </div>
        </>
      )}

      {isMobile && !isMobileMenuOpen && (
        <div className="navbar-user-mobile">
          <NotificationBell />
        </div>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <>
          <div 
            className={`mobile-drawer-overlay ${isMobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            aria-hidden={!isMobileMenuOpen}
          />
          <div 
            className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}
            role="dialog"
            aria-label="Mobile navigation menu"
            aria-modal="true"
          >
            <div className="mobile-drawer-header">
              <div className="mobile-user-info">
                <span className="mobile-user-name">{user?.name || 'User'}</span>
                <span className="mobile-user-role">Tutor</span>
              </div>
              <button
                className="mobile-drawer-close"
                onClick={toggleMobileMenu}
                aria-label="Close navigation menu"
              >
                ✕
              </button>
            </div>
            
            <nav className="mobile-drawer-nav" role="navigation" aria-label="Mobile navigation">
              <button 
                className={activeTab === 'submit' ? 'active' : ''}
                onClick={() => handleTabChange('submit')}
                aria-label="Submit Attendance"
                aria-current={activeTab === 'submit' ? 'page' : undefined}
              >
                Submit Attendance
              </button>
              <button 
                className={activeTab === 'tutees' ? 'active' : ''}
                onClick={() => handleTabChange('tutees')}
                aria-label="My Tutees"
                aria-current={activeTab === 'tutees' ? 'page' : undefined}
              >
                My Tutees
              </button>
              <button 
                className={activeTab === 'lessons' ? 'active' : ''}
                onClick={() => handleTabChange('lessons')}
                aria-label="Lesson Summary"
                aria-current={activeTab === 'lessons' ? 'page' : undefined}
              >
                Lesson Summary
              </button>
              <button 
                className={activeTab === 'profile' ? 'active' : ''}
                onClick={() => handleTabChange('profile')}
                aria-label="My Profile"
                aria-current={activeTab === 'profile' ? 'page' : undefined}
              >
                My Profile
              </button>
              <button 
                className={activeTab === 'claims' ? 'active' : ''}
                onClick={() => handleTabChange('claims')}
                aria-label="Claims"
                aria-current={activeTab === 'claims' ? 'page' : undefined}
              >
                Claims
              </button>
              {user?.financeICSettings?.isEligible && user?.financeICSettings?.isActive && (
                <button 
                  className={activeTab === 'finance-ic-invoices' ? 'active' : ''}
                  onClick={() => handleTabChange('finance-ic-invoices')}
                  aria-label="Finance IC Invoices"
                  aria-current={activeTab === 'finance-ic-invoices' ? 'page' : undefined}
                >
                  Finance IC
                </button>
              )}
              {user?.isCoFounder && (
                <button
                  className={activeTab === 'admin-tutees' ? 'active' : ''}
                  onClick={() => handleTabChange('admin-tutees')}
                  aria-label="All Tutees"
                  aria-current={activeTab === 'admin-tutees' ? 'page' : undefined}
                >
                  All Tutees
                </button>
              )}
            </nav>

            <div className="mobile-drawer-footer">
              <button 
                className="mobile-logout-btn"
                onClick={() => {
                  setIsMobileMenuOpen(false);
                  onLogout();
                }}
                aria-label="Logout"
              >
                Logout
              </button>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};

export default TutorNavbar;